import React from 'react';
import { Ruler, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Компонент для ввода данных кожных складок (калиперометрия)
 */
const SkinfoldMeasurementsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  const [showHelp, setShowHelp] = React.useState(false);
  
  // Проверяем используемый метод Джексона-Поллока
  const isJP3 = formData.measurementMethod === 'jackson_pollock_3';
  const isJP7 = formData.measurementMethod === 'jackson_pollock_7';
  
  // Если ни один из методов калиперометрии не выбран, компонент не отображается
  if (!isJP3 && !isJP7) {
    return null;
  }

  return (
    <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Ruler className="w-5 h-5 mr-2 text-orange-500" />
        Измерения кожных складок
        <button
          type="button"
          onClick={() => setShowHelp(!showHelp)}
          className="ml-2 text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300"
          aria-label={showHelp ? "Скрыть помощь" : "Показать помощь"}
        >
          {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </h3>
      
      {showHelp && (
        <div className="mb-4 p-3 bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-lg">
          <div className="flex items-start">
            <HelpCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-1">
                Как правильно измерять кожные складки
              </h4>
              <p className="text-xs text-orange-600 dark:text-orange-400 mb-2">
                Измерения проводятся с помощью калипера (прибора для измерения толщины кожных складок).
                Все измерения указываются в миллиметрах.
              </p>
              <ul className="text-xs text-orange-600 dark:text-orange-400 space-y-1 list-disc pl-5">
                <li>Грудь: диагональная складка на половине расстояния между подмышечной впадиной и соском</li>
                <li>Живот: вертикальная складка на 2 см правее пупка</li>
                <li>Бедро: вертикальная складка на передней поверхности бедра на середине между паховой складкой и верхушкой надколенника</li>
                {isJP7 && (
                  <>
                    <li>Трицепс: вертикальная складка на задней поверхности плеча на середине между плечевым и локтевым суставами</li>
                    <li>Лопатка (подлопаточная): диагональная складка под углом 45° чуть ниже нижнего угла лопатки</li>
                    <li>Надподвздошная: диагональная складка над гребнем подвздошной кости по передней подмышечной линии</li>
                    <li>Подмышечная: вертикальная складка на уровне мечевидного отростка по средней подмышечной линии</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Основные 3 складки для обоих методов Джексона-Поллока */}
        <div>
          <label htmlFor="chest" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Грудь (мм)
          </label>
          <input
            type="number"
            id="chest"
            name="chest"
            value={formData.chest}
            onChange={handleInputChange}
            min="1"
            max="100"
            step="0.5"
            className={`form-input w-full ${errors.chest ? 'input-error' : ''}`}
            placeholder="Например: 15"
          />
          {errors.chest && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.chest}</p>
          )}
        </div>

        <div>
          <label htmlFor="abdomen" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Живот (мм)
          </label>
          <input
            type="number"
            id="abdomen"
            name="abdomen"
            value={formData.abdomen}
            onChange={handleInputChange}
            min="1"
            max="100"
            step="0.5"
            className={`form-input w-full ${errors.abdomen ? 'input-error' : ''}`}
            placeholder="Например: 20"
          />
          {errors.abdomen && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.abdomen}</p>
          )}
        </div>

        <div>
          <label htmlFor="thigh" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Бедро (мм)
          </label>
          <input
            type="number"
            id="thigh"
            name="thigh"
            value={formData.thigh}
            onChange={handleInputChange}
            min="1"
            max="100"
            step="0.5"
            className={`form-input w-full ${errors.thigh ? 'input-error' : ''}`}
            placeholder="Например: 25"
          />
          {errors.thigh && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.thigh}</p>
          )}
        </div>

        {/* Дополнительные 4 складки для 7-точечного метода Джексона-Поллока */}
        {isJP7 && (
          <>
            <div>
              <label htmlFor="tricep" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Трицепс (мм)
              </label>
              <input
                type="number"
                id="tricep"
                name="tricep"
                value={formData.tricep}
                onChange={handleInputChange}
                min="1"
                max="100"
                step="0.5"
                className={`form-input w-full ${errors.tricep ? 'input-error' : ''}`}
                placeholder="Например: 12"
              />
              {errors.tricep && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.tricep}</p>
              )}
            </div>

            <div>
              <label htmlFor="subscapular" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Лопатка (мм)
              </label>
              <input
                type="number"
                id="subscapular"
                name="subscapular"
                value={formData.subscapular}
                onChange={handleInputChange}
                min="1"
                max="100"
                step="0.5"
                className={`form-input w-full ${errors.subscapular ? 'input-error' : ''}`}
                placeholder="Например: 18"
              />
              {errors.subscapular && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.subscapular}</p>
              )}
            </div>

            <div>
              <label htmlFor="suprailiac" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Надподвздошная (мм)
              </label>
              <input
                type="number"
                id="suprailiac"
                name="suprailiac"
                value={formData.suprailiac}
                onChange={handleInputChange}
                min="1"
                max="100"
                step="0.5"
                className={`form-input w-full ${errors.suprailiac ? 'input-error' : ''}`}
                placeholder="Например: 15"
              />
              {errors.suprailiac && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.suprailiac}</p>
              )}
            </div>

            <div className="md:col-start-2">
              <label htmlFor="midaxillary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Подмышечная (мм)
              </label>
              <input
                type="number"
                id="midaxillary"
                name="midaxillary"
                value={formData.midaxillary}
                onChange={handleInputChange}
                min="1"
                max="100"
                step="0.5"
                className={`form-input w-full ${errors.midaxillary ? 'input-error' : ''}`}
                placeholder="Например: 14"
              />
              {errors.midaxillary && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.midaxillary}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SkinfoldMeasurementsSection;